import { useFormik } from "formik";
import Select from "../../../components/select";
import { useRouterInterfaceList, useSubnetList } from "../layout/resuableEffect";
import * as Yup from "yup";
import Input from "../../../components/input";
import { useDispatch, useSelector } from "react-redux";
import { createNetworkInterface, deleteNetworkInterface, listRouter } from "../../../hooks/local/userReducer";
import { useNavigate } from "react-router-dom";
import TextArea from "../../../components/textArea";
import arrow from "../../../assets/svg-icons/blueArrow.svg";
import close from "../../../assets/svg-icons/closeBrand.svg";
import { useEffect, useMemo, useState } from "react";
import { extractLastNumbers, FormatDate } from "../../../hooks/constants";
import Button from "../../../components/button";
import Modal from "../../../components/modal";
import PaymentModal from "../../../components/paymentModal"; 
import { toast } from "react-toastify"; 
import "react-toastify/dist/ReactToastify.css";


const RouterDetails = () => {

  document.title = "Router Details - Cloudvantage";

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const selectedProject = useSelector((state) => state.user.selectedProject);
  const selectedNetwork = JSON.parse(sessionStorage.getItem("selectedNetwork"));
  const selectedRouter = JSON.parse(sessionStorage.getItem("selectedRouter"));
  const currency = useSelector((state) => state.user.currency);
  const walletBalance = useSelector((state) =>
    state.user.virtualAccount
      ? currency === "USD"
        ? state.user.virtualAccount.dollar_balance
        : state.user.virtualAccount.balance
      : 0
  );
  const virtualAccount = useSelector((state) => state.user.virtualAccount);

  const [router, setRouter] = useState(null);
  const [isFetched, setIsFetched] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [manageInterfaceModal, setManageInterfaceModal] = useState(false);
  const [routerInterface, setRouterInterface] = useState(false);
  const [editNetworkModal, setEditNetworkModal] = useState(false);
  const [deleteInterfaceModal, setDeleteInterfaceModal] = useState(false);
  const [selectedInterface, setSelectedInterface] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false); 

  const handleGetNetwork = async () => {
    if (!selectedRouter?.routerId || !selectedProject?.id) {
      return;
    }

    const routerId = selectedRouter.routerId;
    const projectId = selectedProject.id;

    const { payload } = await dispatch(listRouter({ projectId, routerId }));
    setRouter(payload);
    setIsFetched(true);
  };

  // Call handleGetNetwork immediately if not fetched yet
  if (!isFetched && selectedRouter && selectedProject) {
    handleGetNetwork();
  }

  const [selectedTab, setSelectedTab] = useState("Overview");

  const networkTabs = [
    "Overview",
    "Interfaces",
    // "Static Routes",
  ];

  const overviewDetails = [
    {
      title: "Name",
      value: router?.name || "N/A",
    },
    {
      title: "Router ID",
      value: router?.id || "N/A",
    },
    {
      title: "Project ID",
      value: router?.project_id || "N/A",
    },
    {
      title: "Status",
      value: router?.status || "N/A",
    },
    {
      title: "Admin State",
      value: router?.admin_state_up ? "UP" : "DOWN",
    },
    {
      title: "Availability Zones",
      value: router?.availability_zones?.length > 0 ? router.availability_zones.join(", ") : "N/A",
    },
  ];

  const overviewExternalGatewayDetails = [
    {
      title: "External Network ID",
      value: router?.external_gateway_info?.network_id || "N/A",
    },
    {
      title: "External Fixed IPs",
      value:
        router?.external_gateway_info?.external_fixed_ips?.length > 0
          ? router.external_gateway_info.external_fixed_ips
              .map((ip) => `Subnet: ${ip.subnet_id}, IP: ${ip.ip_address}`)
              .join("; ")
          : "N/A",
    },
    {
      title: "SNAT",
      value: router?.external_gateway_info?.enable_snat ? "Enabled" : "Disabled",
    },
  ];

  const handleSearchChange = (event) => {
    setSearchText(event.target.value);
    setCurrentPage(1);
  };

  const options = [
    { value: "all-interfaces", label: "All interfaces" },
    { value: "name", label: "Interface Name" },
  ];

  const showManageInterfaceModal = (key, data) => {
    setManageInterfaceModal(key);
    const interfaceDetails = { interfaceId: data.id, interfaceName: data.name };
    sessionStorage.setItem("selectedInterface", JSON.stringify(interfaceDetails));
  };

  const closeManageInterfaceModal = () => {
    setManageInterfaceModal(false);
  };

  const toggleInterfaceModal = () => {
    setRouterInterface(!routerInterface);
  };

  const toggleEditRouterModal = (router) => {
    setEditNetworkModal(!editNetworkModal);
  };

  const data = useMemo(
    () => ({
      routerId: selectedRouter?.routerId,
    }),
    [selectedRouter?.routerId]
  );

  const routerInterfaceList = useRouterInterfaceList(selectedProject?.id, data);

  const routerInterfaceListMap = Array.isArray(routerInterfaceList)
    ? routerInterfaceList.map((interfaceData) => ({
        id: interfaceData.id,
        name: `(${extractLastNumbers(interfaceData.id)})`,
        fixedIPs: interfaceData?.device_id || "N/A",
        ipAddress:
          interfaceData.fixed_ips && interfaceData.fixed_ips.length > 0
            ? (interfaceData.fixed_ips[0].ip_address ?? "N/A")
            : "N/A",
        subnetId:
          interfaceData.fixed_ips && interfaceData.fixed_ips.length > 0
            ? (interfaceData.fixed_ips[0].subnet_id ?? "N/A")
            : "N/A",
        status: interfaceData?.status ? "Active" : "Inactive",
        adminState: interfaceData?.admin_state_up ? "UP" : "DOWN",
        createdAt: FormatDate(interfaceData.created_at),
      }))
    : [];

  const [currentPage, setCurrentPage] = useState(1);
  const entriesPerPage = 5;

  const filteredData = routerInterfaceListMap?.filter((row) =>
    row?.name?.toLowerCase().includes(searchText.toLowerCase())
  );

  const indexOfLastEntry = currentPage * entriesPerPage;
  const indexOfFirstEntry = indexOfLastEntry - entriesPerPage;
  const currentEntries = filteredData.slice(indexOfFirstEntry, indexOfLastEntry);
  const totalPages = Math.ceil(filteredData.length / entriesPerPage);

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const params = "";
  const subnetList = useSubnetList(selectedProject?.id, params);

  const usedSubnetIds = routerInterfaceListMap
    .map((interfaceData) => interfaceData.subnetId)
    .filter((subnetId) => subnetId !== "N/A");

  const subnetListOptions = Array.isArray(subnetList)
    ? [
        { label: "Select a subnet", value: "" },
        ...subnetList
          .filter((subnet) => !usedSubnetIds.includes(subnet.id))
          .map((subnet) => ({
            value: subnet.id,
            label: `${subnet.name} : ${subnet?.cidr}`,
          })),
      ]
    : [{ label: "Select a subnet", value: "" }];

  const createInterface = useFormik({
    initialValues: {
      subnetId: "",
      paymentMode: "monthly",
      description: "",
    },
    validationSchema: Yup.object({
      subnetId: Yup.string().required("Subnet ID cannot be empty"),
      paymentMode: Yup.string().required("Payment Mode is required"),
    }),
    onSubmit: async (values, { resetForm }) => {
      try {
        const { subnetId, description, paymentMode } = values;

        // Calculate total cost
        const IPAddressPrice = currency === "NGN" ? 9000 : 6.0;
        const totalCost = paymentMode === "yearly" ? IPAddressPrice * 12 : IPAddressPrice;

        // Check if wallet balance is sufficient
        if (totalCost > walletBalance) {
          setShowPaymentModal(true);
          return;
        }

        let interfaceData = {
          subnet_id: subnetId,
          projectId: selectedProject?.id,
          routerId: selectedRouter?.routerId,
          payment_mode: paymentMode,
          // description,
        };

        const { payload } = await dispatch(createNetworkInterface(interfaceData));
        if (payload.statuscode === "00") {
          resetForm();
          toggleInterfaceModal();
          window.location.reload();
        } else {
          toast.error(`Failed to create interface: ${payload?.message || "Unknown error"}`);
        }
      } catch (error) {
        toast.error("An unexpected error occurred during interface creation.");
        console.error(error);
      }
    },
  });

  const [calculatedEstimate, setCalculatedEstimate] = useState("0.00");

  const calculateEstimatedCost = (paymentMode) => {
    const IPAddressPrice = currency === "NGN" ? 9000 : 6.0;

    let totalCost = IPAddressPrice;

    if (paymentMode === "yearly") {
      const yearlyPrice = totalCost * 12;
      totalCost = yearlyPrice;
    }

    return totalCost.toLocaleString("en-US", {
      style: "currency",
      currency: currency === "NGN" ? "NGN" : "USD",
    });
  };

  useEffect(() => {
    const estimate = calculateEstimatedCost(createInterface?.values?.paymentMode);
    setCalculatedEstimate(estimate);
  }, [createInterface?.values?.paymentMode, currency]);

  const handleServerAction = async (action, subnetId) => {
    const projectId = selectedProject.id;

    const data = {
      projectId,
      routerId: selectedRouter?.routerId,
      subnet_id: subnetId,
    };

    const { payload } = await dispatch(action({ projectId, data }));

    if (payload.statuscode === "00") {
      window.location.reload();
    } else {
      toast.error(`Failed to delete interface: ${payload?.message || "Unknown error"}`);
    }
  };

  const deleteInterface = (subnetId) => handleServerAction(deleteNetworkInterface, subnetId);

  return (
    <div className="min-h-screen px-4 md:px-10 pt-6 pb-10 dark:text-white">
      <p className="crimson-font text-2xl font-medium text-primaryBlue mb-10">
        Router Details for {selectedRouter?.routerName}
      </p>

      <div className="flex flex-col justify-start items-start w-full border-b dark:border-b-gray-500 border-b-gray-400">
        <div className="flex justify-start items-center w-full md:w-[50%]">
          {networkTabs.map((tab, i) => (
            <button
              key={i}
              className={`w-full p-4 ${selectedTab === tab ? "bg-primaryBlue text-white rounded-md" : "text-primaryBlue"} text-sm`}
              onClick={() => setSelectedTab(tab)}
            >
              {tab}
            </button>
          ))}
        </div>
      </div>

      {selectedTab === "Overview" && (
        <div className="mt-8 grid gap-8">
          <div className="bg-white dark:bg-[#141D26] rounded-md w-full py-6 grid gap-4 px-10 border-t-4 border-t-primaryBlue border border-primaryBlue">
            <h2 className="text-2xl font-bold text-primaryBlue">Basic Information</h2>
            {overviewDetails.map((item, i) => (
              <div
                key={i}
                className="w-full flex lg:flex-row flex-col justify-start lg:justify-between items-start lg:items-center gap-2 lg:gap-6"
              >
                <h2 className="text-xl font-bold text-black">{item?.title}</h2>
                <p className="text-base text-gray-600">{item?.value}</p>
              </div>
            ))}
          </div>

          {router?.external_gateway_info && (
            <div className="bg-white dark:bg-[#141D26] rounded-md w-full py-6 grid gap-4 px-10 border-t-4 border-t-primaryBlue border border-primaryBlue">
              <h2 className="text-2xl font-bold text-primaryBlue">External Gateway Information</h2>
              {overviewExternalGatewayDetails.map((item, i) => (
                <div
                  key={i}
                  className="w-full flex lg:flex-row flex-col justify-start lg:justify-between items-start lg:items-center gap-2 lg:gap-6"
                >
                  <h3 className="text-lg font-semibold text-black dark:text-white">{item?.title}</h3>
                  <p className="text-base text-gray-600 dark:text-gray-300">{item?.value}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {selectedTab === "Interfaces" && (
        <div>
          {routerInterfaceListMap?.length > 0 ? (
            <div>
              <div className="w-full flex">
                <div className="w-full grid grid-cols-1 md:grid-cols-12 gap-4 items-end my-10">
                  <div className="md:col-span-4">
                    <Select label="Filter interfaces" name="interfaces" options={options} />
                  </div>
                  <div className="md:col-span-4">
                    <input
                      type="search"
                      name=""
                      value={searchText}
                      onChange={handleSearchChange}
                      placeholder="Search for interfaces..."
                      className="bg-white text-black w-full p-4 text-sm rounded-md border border-[#c4c4c460] active:outline-0 focus:outline-0 placeholder:opacity-70 placeholder:text-xs dark:bg-slate-800 dark:text-white"
                    />
                  </div>
                </div>

                <div className="w-full flex items-center justify-end">
                  <button
                    className="cursor-pointer px-4 py-3 text-xs border-[1.5px] border-primaryBlue rounded-md text-primaryBlue w-fit"
                    onClick={toggleInterfaceModal}
                  >
                    Create interface
                  </button>
                </div>
              </div>

              <div className="overflow-x-auto rounded-md border dark:border-[#c4c4c432]">
                <table className="w-full text-sm text-left overflow-hidden">
                  <thead className="bg-white dark:bg-slate-800 px-3 md:px-4 border text-gray-400 dark:border-[#c4c4c432] border-b text-sm font-semibold">
                    <tr>
                      <th scope="col" className="px-6 py-4 whitespace-nowrap w-5">
                        #
                      </th>
                      <th scope="col" className="px-6 py-4 whitespace-nowrap max-w-52">
                        Interface name
                      </th>
                      <th scope="col" className="px-6 py-4 whitespace-nowrap">
                        Fixed IPs
                      </th>
                      <th scope="col" className="px-6 py-4 whitespace-nowrap">
                        Status
                      </th>
                      <th scope="col" className="px-6 py-4 whitespace-nowrap">
                        Admin State
                      </th>
                      <th scope="col" className="px-6 py-4 whitespace-nowrap">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-slate-800 px-3 md:px-4 border rounded-b-md dark:border-[#c4c4c432] border-t-2 text-sm">
                    {currentEntries.map((row, index) => (
                      <tr
                        key={index}
                        className="dark:hover:bg-[#ffffff05] hover:bg-brandBlue/25 whitespace-nowrap"
                      >
                        <td className="text-start py-4 px-6">{index + 1}.</td>
                        <td className="text-start py-4 px-6">{row.name}</td>
                        <td className="text-start py-4 px-6">{row.ipAddress}</td>
                        <td className="text-start py-4 px-6">{row.status}</td>
                        <td className="text-start py-4 px-6">{row.adminState}</td>
                        <td className="text-start py-4 px-6">
                          <button
                            className="text-white p-3 text-xs rounded bg-red-500 w-full md:w-fit"
                            onClick={() => setDeleteInterfaceModal(true)}
                          >
                            Delete Interface
                          </button>

                          <Modal
                            title={"Delete Interface"}
                            isVisible={deleteInterfaceModal}
                            onClose={() => setDeleteInterfaceModal(false)}
                          >
                            <div className="text-sm text-center pt-6 text-wrap">
                              Are you sure you want to delete{" "}
                              <span className="underline">{row.name}</span>. This action cannot be undone.
                            </div>
                            <div className="grid gap-4 justify-center py-6">
                              <button
                                className="bg-red-500 text-white text-xs px-6 py-4 rounded-sm w-fit"
                                onClick={() => deleteInterface(row.subnetId)}
                              >
                                Yes, delete {row.name}
                              </button>
                              <button
                                className="text-[#1DA1F2] text-sm px-6 py-2 hover:underline"
                                onClick={() => setDeleteInterfaceModal(false)}
                              >
                                Cancel
                              </button>
                            </div>
                          </Modal>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="grid justify-center gap-4 md:flex md:justify-between items-center mt-4">
                <p className="text-gray-400 text-sm">
                  Showing{" "}
                  <span className="font-medium text-black dark:text-white">
                    {indexOfFirstEntry + 1}
                  </span>{" "}
                  to{" "}
                  <span className="font-medium text-black dark:text-white">
                    {Math.min(indexOfLastEntry, routerInterfaceListMap?.length)}
                  </span>{" "}
                  of{" "}
                  <span className="font-medium text-black dark:text-white">
                    {routerInterfaceListMap?.length}
                  </span>{" "}
                  results
                </p>
                <div className="flex gap-3">
                  <button
                    className={`bg-white dark:bg-primaryBlue border dark:border-none rounded-md py-3 px-5 text-sm ${
                      currentPage === 1 ? "opacity-50" : "opacity-100"
                    }`}
                    onClick={handlePreviousPage}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </button>
                  <button
                    className={`bg-white dark:bg-primaryBlue border dark:border-none rounded-md py-3 px-5 text-sm ${
                      currentPage === totalPages ? "opacity-50" : "opacity-100"
                    }`}
                    onClick={handleNextPage}
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="md:px-52 text-center grid text-gray-500 mt-32">
              <div>It looks like you don't have interfaces yet. Once you create an interface, your interfaces will appear here.</div>
              <div
                className="border border-[#c4c4c450] dark:border-[#c4c4c416] rounded-md p-3 flex items-center justify-self-center mt-8 w-fit text-xs cursor-pointer"
                onClick={toggleInterfaceModal}
              >
                <span>Create Interface</span>
                <span className="ps-2">
                  <img src={arrow} alt="" />
                </span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Create Router Interface */}
      {routerInterface && (
        <div className="fixed top-0 h-screen w-full left-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-[#141D26] rounded-md p-4 md:p-10 w-full md:w-1/3 max-h-[80%] overflow-y-scroll">
            <div className="flex items-center justify-between mb-4">
              <p className="text-primaryBlue">Create Interface</p>
              <span onClick={toggleInterfaceModal} className="cursor-pointer">
                <img alt="" src={close} className="h-5" />
              </span>
            </div>

            <form className="gap-y-6 grid" onSubmit={createInterface.handleSubmit}>
              <Select
                name="subnetId"
                label="Select a Subnet *"
                options={subnetListOptions}
                value={createInterface.values.subnetId}
                onBlur={createInterface.handleBlur}
                onChange={createInterface.handleChange}
                onError={
                  createInterface.touched.subnetId && createInterface.errors.subnetId
                    ? createInterface.errors.subnetId
                    : null
                }
              />

              <Select
                label={"Payment Method"}
                type={"text"}
                name={"paymentMode"}
                options={[
                  { label: "Select payment method", value: "" },
                  { label: "Monthly", value: "monthly" },
                  { label: "Yearly", value: "yearly" },
                ]}
                placeholder={"Select payment method"}
                value={createInterface.values.paymentMode}
                onBlur={createInterface.handleBlur}
                onChange={createInterface.handleChange}
                onError={
                  createInterface.errors.paymentMode && createInterface.touched.paymentMode
                    ? createInterface.errors.paymentMode
                    : null
                }
              />

              <TextArea
                label={"Description (optional)"}
                type={"text"}
                name={"description"}
                value={createInterface.values.description}
                onBlur={createInterface.handleBlur}
                onChange={createInterface.handleChange}
                onError={
                  createInterface.errors.description && createInterface.touched.description
                    ? createInterface.errors.description
                    : null
                }
              />

              {createInterface.values.subnetId && createInterface.values.paymentMode && (
                <div className="grid gap-2">
                  <div className="text-xs">Estimated cost:</div>
                  <div className="font-semibold crimson-font text-primaryBlue text-lg">
                    {calculatedEstimate}
                  </div>
                </div>
              )}

              <div className="flex justify-between items-center gap-2">
                <Button
                  buttonText={
                    createInterface.values.subnetId &&
                    createInterface.values.paymentMode &&
                    (createInterface.values.paymentMode === "monthly"
                      ? currency === "NGN"
                        ? 9000 > walletBalance
                        : 6 > walletBalance
                      : currency === "NGN"
                        ? 9000 * 12 > walletBalance
                        : 6 * 12 > walletBalance)
                      ? "Pay Now"
                      : "Create Interface"
                  }
                  buttonRole={"submit"}
                  type="submit"
                />
              </div>
            </form>
          </div>
        </div>
      )}

      <PaymentModal
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        currency={currency}
        totalPrice={
          createInterface.values.paymentMode === "yearly"
            ? currency === "NGN"
              ? 9000 * 12
              : 6 * 12
            : currency === "NGN"
              ? 9000
              : 6
        }
        walletBalance={walletBalance}
        virtualAccount={virtualAccount}
        onPaymentSuccess={() => createInterface.handleSubmit()}
      />

    </div>
  );
};

export default RouterDetails;